import React, { useState, useEffect } from 'react';
import {
  generateStudentReportCard,
  getStudentReportCard,
  getStudentBadges,
  getUserTopicPerformance
} from '../components/apiClient';
import { useJWTAuth } from '../components/JWTAuthContext';
import {
  QuizProgressChart,
  TopicPerformanceChart,
  QuizPerformance,
  ChapterPerformance,
  QuickStatsOverview,
  SubjectWiseQuestions,
  LearningInsights,
  SubjectPerformance,
  AdditionalStatistics,
  AdditionalChartsRow
} from '../components/report-card';

const ReportCard: React.FC = () => {
  const { user } = useJWTAuth();
  const [reportCard, setReportCard] = useState<any>(null);
  const [badges, setBadges] = useState<any>(null);
  const [topicPerformance, setTopicPerformance] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastGenerated, setLastGenerated] = useState<string | null>(null);

  useEffect(() => {
    const loadExistingReportCard = async () => {
      if (!user?.id) return;

      setIsLoading(true);
      setError(null);

      try {
        // Try to load existing report card
        const [reportResponse, badgesResponse, topicResponse] = await Promise.all([
          getStudentReportCard(),
          getStudentBadges(),
          getUserTopicPerformance()
        ]);

        if (reportResponse?.result) {
          setReportCard(reportResponse.result);
          setLastGenerated(reportResponse.result.generated_at);
        }

        if (badgesResponse?.result) {
          setBadges(badgesResponse.result);
        }

        if (topicResponse?.result) {
          setTopicPerformance(topicResponse.result);
        }
      } catch (error: any) {
        console.error('Failed to load existing report card:', error);
        // Don't show error for missing report card - user can generate one
      } finally {
        setIsLoading(false);
      }
    };

    loadExistingReportCard();
  }, [user]);

  const handleGenerateReport = async () => {
    if (!user?.id) return;

    setIsGenerating(true);
    setError(null);

    try {
      // Pass the user ID to the generation function
      const response = await generateStudentReportCard(user.id);

      if (response?.result) {
        setReportCard(response.result.report_card);
        setBadges(response.result.badges_earned || []);
        setLastGenerated(response.result.report_generated_at);
      }

      // Automatically reload the report card data after generation
      try {
        const [reportResponse, badgesResponse, topicResponse] = await Promise.all([
          getStudentReportCard(),
          getStudentBadges(),
          getUserTopicPerformance()
        ]);

        if (reportResponse?.result) {
          setReportCard(reportResponse.result);
          setLastGenerated(reportResponse.result.generated_at);
        }

        if (badgesResponse?.result) {
          setBadges(badgesResponse.result);
        }

        if (topicResponse?.result) {
          setTopicPerformance(topicResponse.result);
        }
      } catch (reloadError) {
        console.error('Failed to reload report card data after generation:', reloadError);
        // Don't fail the whole operation if reload fails - we still have the generated data
      }
    } catch (error: any) {
      console.error('Failed to generate report card:', error);
      setError(error.message || 'Failed to generate report card. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ backgroundColor: '#f8f9ff' }}>
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-muted">Loading your report card...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-vh-100" style={{ backgroundColor: '#f8f9ff', paddingTop: '0px' }}>
      <div className="container py-5">
        <div className="row">
          <div className="col-12">
            {/* Header Section */}
            <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '20px' }}>
              <div className="card-body p-4" style={{
                background: 'linear-gradient(135deg, #667eea, #764ba2)',
                borderRadius: '20px'
              }}>
                <div className="row align-items-center">
                  <div className="col-md-8">
                    <h1 className="text-white fw-bold mb-2">
                      📊 Your Learning Report Card
                    </h1>
                    <p className="text-white opacity-90 mb-0 fs-5">
                      Track your progress, celebrate achievements, and identify areas for improvement.
                    </p>
                    {lastGenerated && (
                      <small className="text-white opacity-75">
                        Last updated: {formatDate(lastGenerated)}
                      </small>
                    )}
                  </div>
                  <div className="col-md-4 text-end">
                    <button
                      className="btn btn-light btn-lg px-4"
                      onClick={handleGenerateReport}
                      disabled={isGenerating}
                      style={{ borderRadius: '15px' }}
                    >
                      {isGenerating ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                          Generating...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-refresh me-2"></i>
                          {reportCard ? 'Refresh Report' : 'Generate Report'}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="alert alert-danger alert-dismissible fade show" role="alert">
                <i className="fas fa-exclamation-triangle me-2"></i>
                {error}
                <button type="button" className="btn-close" onClick={() => setError(null)}></button>
              </div>
            )}

            {/* No Report Card Message */}
            {!reportCard && !isLoading && !isGenerating && (
              <div className="card border-0 shadow-sm text-center py-5" style={{ borderRadius: '20px' }}>
                <div className="card-body">
                  <i className="fas fa-chart-line text-muted mb-4" style={{ fontSize: '4rem' }}></i>
                  <h3 className="text-muted mb-3">No Report Card Available</h3>
                  <p className="text-muted mb-4">
                    Generate your personalized report card to see detailed insights about your learning progress.
                  </p>
                  <button
                    className="btn btn-primary btn-lg px-5"
                    onClick={handleGenerateReport}
                    disabled={isGenerating}
                    style={{ borderRadius: '15px' }}
                  >
                    <i className="fas fa-chart-bar me-2"></i>
                    Generate My Report Card
                  </button>
                </div>
              </div>
            )}

            {/* Report Card Content */}
            {reportCard && (
              <>
                {/* Quick Stats Overview */}
                <QuickStatsOverview reportCard={reportCard} formatTime={formatTime} />

                {/* Subject-wise Questions Attempted */}
                <SubjectWiseQuestions reportCard={reportCard} />

                {/* Quiz Progress Chart */}
                {reportCard.quiz_progress_data && Array.isArray(reportCard.quiz_progress_data) && reportCard.quiz_progress_data.length > 0 && (
                  <div className="row g-4 mb-4">
                    <div className="col-12">
                      <QuizProgressChart data={reportCard.quiz_progress_data} />
                    </div>
                  </div>
                )}

                {/* Additional Charts Row */}
                <AdditionalChartsRow reportCard={reportCard} />



                {/* Subject Performance & Difficulty Analysis */}
                <SubjectPerformance reportCard={reportCard} formatTime={formatTime} />

                {/* Learning Insights */}
                {/* <LearningInsights reportCard={reportCard} badges={badges} /> */}

                {/* Additional Statistics */}
                <AdditionalStatistics reportCard={reportCard} formatTime={formatTime} />

                {/* Quiz Performance History */}
                <div className="row g-4 mb-4">
                  <div className="col-md-12">
                    <QuizPerformance />
                  </div>
                </div>


                {/* Chapter Performance Analysis */}
                <div className="row g-4 mb-4">
                  <div className="col-md-12">
                    <ChapterPerformance />
                  </div>
                </div>

                {/* Topic Coverage */}
                {topicPerformance && topicPerformance.subjects && topicPerformance.subjects.length > 0 && (
                  <div className="row g-4 mb-4">
                    <div className="col-md-12">
                      <TopicPerformanceChart data={topicPerformance} />
                    </div>
                  </div>
                )}

                {/* Data Period Information */}
                <div className="row g-4 mb-4">
                  <div className="col-md-12">
                    <div className="card border-0 shadow-sm bg-light" style={{ borderRadius: '15px' }}>
                      <div className="card-body text-center py-3">
                        <small className="text-muted">
                          <i className="fas fa-calendar me-2"></i>
                          Report covers data from {formatDate(reportCard.data_period_start)} to {formatDate(reportCard.data_period_end)}
                          {reportCard.total_badges_earned && (
                            <span className="ms-3">
                              <i className="fas fa-medal me-1"></i>
                              {reportCard.total_badges_earned} badges earned
                            </span>
                          )}
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportCard;
